import React, { useEffect, useRef } from "react";
import { Renderer, Program, Mesh, Triangle } from "ogl";

const vertex = /* glsl */ `
  attribute vec2 position;
  attribute vec2 uv;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform vec2 uResolution;
  uniform float uIntensity;
  uniform float uGrain;
  varying vec2 vUv;

  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 6; i++) {
      value += amplitude * noise(p);
      p *= 2.05;
      amplitude *= 0.48;
    }
    return value;
  }

  vec3 palette(float t) {
    vec3 dark   = vec3(0.025, 0.045, 0.035);
    vec3 forest = vec3(0.04,  0.22,  0.14);
    vec3 lime   = vec3(0.55,  0.92,  0.34);
    vec3 yellow = vec3(0.90,  0.82,  0.38);
    vec3 cyan   = vec3(0.20,  0.76,  0.86);
    vec3 teal   = vec3(0.04,  0.34,  0.32);
    vec3 color = dark;
    color = mix(color, forest, smoothstep(0.05, 0.35, t));
    color = mix(color, teal,   smoothstep(0.25, 0.55, t));
    color = mix(color, lime,   smoothstep(0.42, 0.72, t));
    color = mix(color, yellow, smoothstep(0.58, 0.82, t));
    color = mix(color, cyan,   smoothstep(0.72, 1.0,  t));
    return color;
  }

  void main() {
    vec2 uv = vUv;
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 p = (uv - 0.5) * aspect;
    float time = uTime * 0.055;

    vec2 flow = vec2(
      fbm(p * 1.45 + vec2(time * 0.55, -time * 0.20)),
      fbm(p * 1.45 + vec2(-time * 0.25,  time * 0.45) + 4.2)
    );
    p += (flow - 0.5) * 0.48;

    float fieldA = fbm(p * 1.15 + vec2(time * 0.35,  time * 0.12));
    float fieldB = fbm(p * 2.10 - vec2(time * 0.18,  time * 0.28));
    float fieldC = fbm(p * 3.25 + vec2(sin(time) * 0.4, cos(time) * 0.35));
    float field  = fieldA * 0.56 + fieldB * 0.31 + fieldC * 0.13;

    field += exp(-length(p - vec2(-0.45,  0.08)) * 1.95) * 0.34;
    field += exp(-length(p - vec2( 0.38,  0.23)) * 2.25) * 0.28;
    field += exp(-length(p - vec2( 0.02, -0.34)) * 2.00) * 0.30;
    field += exp(-length(p - vec2( 0.62, -0.16)) * 2.70) * 0.18;
    field  = smoothstep(0.18, 1.18, field);

    vec3 color = palette(field);

    float vignette = smoothstep(0.95, 0.22, length((uv - 0.5) * vec2(1.15, 1.0)));
    color *= mix(0.42, 1.18, vignette);

    float glow = smoothstep(0.45, 1.0, field);
    color += glow * vec3(0.10, 0.22, 0.16) * uIntensity;

    color = mix(color, vec3(0.018, 0.024, 0.022), 0.20);

    float grain = hash(uv * uResolution.xy + fract(uTime) * 1000.0);
    color += (grain - 0.5) * uGrain;

    float gray = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(gray), color, 0.86);

    gl_FragColor = vec4(color, 1.0);
  }
`;

export default function AnimatedBackground() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      alpha: false,
      antialias: false,
    });

    const gl = renderer.gl;
    gl.clearColor(0.015, 0.02, 0.018, 1);
    container.appendChild(gl.canvas);

    const geometry = new Triangle(gl);
    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uTime:       { value: 0 },
        uResolution: { value: [window.innerWidth, window.innerHeight] },
        uIntensity:  { value: 0.85 },
        uGrain:      { value: 0.035 },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });

    const resize = () => {
      const width  = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width, height);
      program.uniforms.uResolution.value = [width, height];
    };

    let animId;
    const startTime = performance.now();
    const update = () => {
      program.uniforms.uTime.value = (performance.now() - startTime) / 1000;
      renderer.render({ scene: mesh });
      animId = requestAnimationFrame(update);
    };

    resize();
    update();
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
      gl.canvas.remove();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
    />
  );
}

